import { gql } from "@apollo/client";

// Shared asset row fields (matches AssetType in the API schema).
const ADMIN_ASSET_FIELDS = `
  assetId
  serialNumber
  name
  hostname
  platform
  osName
  osVersion
  model
  diskEncryption
  firewallEnabled
  mdmEnabled
  agentInstalled
  agentVersion
  edrInstalled
  edrStatus
  patchStatus
  enrollmentStatus
  ownerEmployeeId
  ownerEmail
  ownerName
  lastActivityAt
  source
  integratedAccountId
  integrationName
  status
  currentStatus
  inactivityReason
  deviceType
  description
  disposableDate
  # Admin-controlled compliance scope, per physical device. Out-of-scope assets
  # are still returned by the API (flagged, not hidden) so they can be shown
  # greyed out and restored.
  inScope
  scopeReason
`;

const CHECK_STATUS_FIELDS = `
  checkStatus {
    assetId
    totalChecksCount
    passedChecksCount
    failedChecksCount
    disabledChecksCount
    checks {
      activeCheckId
      activeCheckName
      checkType
      status
      statusReason
      updatedAt
    }
  }
`;

export const GET_ALL_ASSETS = gql`
  query getAllAssets {
    getAllAssets {
      ${ADMIN_ASSET_FIELDS}
      ${CHECK_STATUS_FIELDS}
    }
  }
`;

export const GET_ASSETS_BY_EMPLOYEE = gql`
  query getAssetsByEmployee($employeeDataId: String!) {
    getAssetsByEmployee(employeeDataId: $employeeDataId) {
      ${ADMIN_ASSET_FIELDS}
    }
  }
`;

export const GET_ASSET_CHECKS = gql`
  query getAssetChecks($activeCheckCategory: String) {
    getAssetChecks(activeCheckCategory: $activeCheckCategory) {
      activeCheckId
      activeCheckName
      activeCheckDescription
      activeCheckCategory
      activeCheckType
      monitorType
    }
  }
`;

// Manually registers an asset from the "Add Asset" dialog.
// `disposableDate` is an ISO datetime string ("YYYY-MM-DDTHH:mm:ssZ") or null.
export const CREATE_MANUAL_ASSET = gql`
  mutation CreateManualAsset(
    $name: String!
    $serialNumber: String!
    $ownerEmployeeId: String!
    $deviceType: String!
    $hostname: String
    $os: String
    $osVersion: String
    $model: String
    $description: String
    $disposableDate: String
  ) {
    createManualAsset(
      name: $name
      serialNumber: $serialNumber
      ownerEmployeeId: $ownerEmployeeId
      deviceType: $deviceType
      hostname: $hostname
      os: $os
      osVersion: $osVersion
      model: $model
      description: $description
      disposableDate: $disposableDate
    ) {
      success
      message
      data {
        assetId
        dataStoreId
        serialNumber
        name
        hostname
        deviceType
        osName
        osVersion
        model
        description
        disposableDate
        ownerEmployeeId
      }
    }
  }
`;

// Updates the editable fields of an asset from the details dialog.
export const UPDATE_ASSET_DETAILS = gql`
  mutation UpdateAssetDetails(
    $assetId: String!
    $deviceType: String
    $description: String
    $disposableDate: String
  ) {
    updateAssetDetails(
      assetId: $assetId
      deviceType: $deviceType
      description: $description
      disposableDate: $disposableDate
    ) {
      success
      message
      data {
        assetId
        serialNumber
        deviceType
        description
        disposableDate
      }
    }
  }
`;

// Marks an asset in / out of the compliance scope. Admin-only server-side, and
// deliberately separate from UPDATE_ASSET_DETAILS, which employees may also call.
// Scope is per physical device (keyed by serial), so one call covers every
// source and owner. `scopeReason` is required when marking out of scope and is
// cleared when restoring.
export const UPDATE_ASSET_SCOPE = gql`
  mutation UpdateAssetScope(
    $assetId: String!
    $inScope: Boolean!
    $scopeReason: String
  ) {
    updateAssetScope(
      assetId: $assetId
      inScope: $inScope
      scopeReason: $scopeReason
    ) {
      success
      message
      data {
        assetId
        serialNumber
        inScope
        scopeReason
        scopeUpdatedAt
      }
    }
  }
`;

// Reassigns a device to a different employee. Admin-only server-side: the owner
// is who gets chased for the device's failing checks, so it isn't theirs to
// change. Pass a null ownerEmployeeId to leave the device unassigned. The API
// versions the change (closes the current row, opens a new one), so ownership
// history is preserved rather than overwritten.
export const UPDATE_ASSET_OWNER = gql`
  mutation UpdateAssetOwner($assetId: String!, $ownerEmployeeId: String) {
    updateAssetOwner(assetId: $assetId, ownerEmployeeId: $ownerEmployeeId) {
      success
      message
      data {
        assetId
        serialNumber
        ownerEmployeeId
        ownerEmail
        ownerName
      }
    }
  }
`;

// Deletes a manually-added asset. Admin-only, and the API refuses anything Rego
// WatchTower or an MDM still reports — those would reappear on the next sync and
// should be marked out of scope instead.
export const DELETE_MANUAL_ASSET = gql`
  mutation DeleteManualAsset($assetId: String!) {
    deleteManualAsset(assetId: $assetId) {
      success
      message
      data {
        assetId
        serialNumber
        deletedAt
      }
    }
  }
`;

export const GET_ASSET_CHECK_STATUSES = gql`
  query getAssetCheckStatuses($assetId: String) {
    getAssetCheckStatuses(assetId: $assetId) {
      assetId
      totalChecksCount
      passedChecksCount
      failedChecksCount
      disabledChecksCount
      checks {
        activeCheckId
        checkType
        status
        statusReason
        createdAt
        updatedAt
      }
    }
  }
`;
